import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { TextGridBlock } from 'stile-shared/src/components/2020/blocks/TextGridBlock';
import { Diagram } from 'stile-shared/src/components/2020/common/Diagram';
import { P } from 'stile-shared/src/components/2020/common/Primitives';
import { Spacer } from 'stile-shared/src/components/2020/common/Primitives';
import { Quote } from 'stile-shared/src/components/2020/common/Quote';

import { getPageData } from 'utils/getPageData';

import { theme } from 'styles/theme';

import { PageLayout } from 'templates/2020/PageLayout';

import { NextPageBlock } from 'components/blocks/NextPageBlock';

export { Head } from 'templates/2020/Head';

function EvidenceBasedPedagogyPage(props: PageProps) {
  const { hero } = getPageData(props.location.pathname);

  return (
    <PageLayout {...props}>
      <HeroBlock
        {...hero}
        image={
          <StaticImage
            loading="eager"
            aspectRatio={2.37}
            src="../../../../stile-shared/assets/images/evidence-based-pedagogy/cover_evidence-based_pedagogy.png"
            alt="A teacher and student in the midst of a classroom discussing his work"
          />
        }
      />

      <TextBlock>
        <P>
          Stile’s resources are meticulously constructed around pedagogies that have been
          demonstrated to work. No fads.
        </P>
        <P>
          Drawing on the best ideas from John Hattie, Robert Marzano, Dylan Wiliam, Jean Piaget and
          others, we make it easier for teachers to put high-impact, evidence-based teaching
          strategies into practice.
        </P>
        <Spacer />
        <Spacer />
        <Quote
          text="Stile is the resource I’d always imagined. It blends learning styles and quality pedagogy, provides collaboration opportunities, and allows for timely and effective feedback for both students and staff. Most importantly, it keeps getting better."
          credit="Chris Ferry, Magdalene Catholic College, NSW"
        />
      </TextBlock>

      <TeaserBlock
        variant="beige"
        title="Tried and tested teaching and learning strategies embedded at every level"
        image={
          <Diagram
            image={
              /* alt text is not required because more detailed screen reader text is added below. This image and the alt text needs to be updated before publishing. */
              <StaticImage
                src="../../../../stile-shared/assets/images/evidence-based-pedagogy/pedagogy_diagram.png"
                alt=""
              />
            }
            screenReaderText={[
              'Set learning goals',
              'Engage students with real-world relevance',
              'Direct instruction - Feedback - Self-paced formative assesment',
              'Metacognition',
              'Use data to inform next lessons and differentiate',
            ]}
          />
        }
      >
        <P>
          Great pedagogical practice in the classroom starts with precisely constructed learning
          materials. From crafting learning goals and carefully scaffolding each lesson, to digital
          tools that make timely, specific feedback a breeze, Stile is the ideal foundation for
          excellent science teaching.
        </P>
        <P>
          Pedagogy also drives the types of curriculum materials we create. Exploration of complex
          global issues and high-order thinking tasks are designed for class. Meanwhile, lower-order
          "memorisation", important in unlocking the scientific vocabulary needed to engage in the
          classroom, is gamified via a phone app.
        </P>
        <P>
          Research suggests that revision is more effective when handwritten. Brimming with revision
          and consolidation activities, our printed workbooks are designed to be used by students
          for individual learning or at home.
        </P>
      </TeaserBlock>

      <TextGridBlock
        title="The high-impact teaching and learning strategies incorporated in Stile"
        items={[
          {
            title: 'The 5E Model',
            text: 'Stile has been created with the 5E Model (Bybee, et al., 2006) front of mind, and each of the phases - engage, explore, explain, elaborate and evaluate - can be easily identified in our resources.',
          },
          {
            title: 'Explicit teaching',
            text: 'Stile is a true blended learning tool. Our digital lessons are made for teachers to use at the front of the classroom. Teachers are provided with rigorous teaching notes, which encourage them to explicitly teach new concepts and actively engage with their students as they work.',
          },
          {
            title: 'High expectations',
            text: 'Having low expectations of our students has been shown to lower achievement (Khattab, 2015). Stile’s resources are appropriately challenging at the year level - we expect students to think! - with plenty of scaffolding and support.',
          },
          {
            title: 'Explicit learning goals',
            text: 'Evidence shows that students who know what is expected of them are more likely to engage in the learning process (Hattie, 2012). Every Stile lesson identifies one to three learning goals that students should know, understand and be able to do by the end of that lesson.',
          },
          {
            title: 'Metacognition',
            text: 'Students are continually encouraged to build an understanding of and reflect upon the process of their own learning (Perry, et al., 2019), often through Visible Thinking routines (Ritchhart et al, 2011). As they work through any unit, Stile provides the tools to help students consider both their strengths and their next steps toward reaching learning goals.',
          },
          {
            title: 'SOLO Taxonomy',
            text: 'All of Stile’s lessons are carefully scaffolded from lower- to higher-order thinking as per SOLO Taxonomy. As students progress through a lesson, they are challenged, finding their Zone of Proximal Development.',
          },
          {
            title: 'Student-designed solutions',
            text: 'Stile draws direct links between real-world technologies and the science that underpins them. In all Stile units, your students engage in phenomena, ask questions, and design possible solutions anchored by the real world (National Research Council, 2012).',
          },
          {
            title: 'Students engage in real-world phenomena',
            text: 'Every Stile unit is centred around an intriguing phenomenon, intentionally selected to motivate your students’ curiosity. Upon their introduction to the anchoring phenomenon, students are driven to ask questions and seek answers that explain it (National Research Council 2012).',
          },
          {
            title: 'Collaborative problem-solving',
            text: 'Stile is designed to help students and teachers work cooperatively to explore and unpack the anchoring phenomenon. Students can express their ideas in pairs, small groups, and whole-class siutations throughout lessons, practical activities, Socratic seminars and engineering challenges.',
          },
          {
            title: 'Handwritten notes',
            text: 'Research suggests that handwritten notes have a higher impact on cognitive understanding, recall and grades (Mueller & Oppenheimer, 2014). Stile’s printed resources support students in taking high-quality notes and consolidating key scientific knowledge.',
          },
          {
            title: 'Timely, specific feedback',
            text: 'We know the importance of students receiving immediate feedback to support learning (Skipper & Douglas, 2015). Stile’s unique user interface encourages teachers to review class responses in real time, supported by fully worked model answers, assessment rubrics and teaching notes.',
          },
          {
            title: 'Worked examples',
            text: 'Every single question in Stile has fully worked model answers and, where relevant, worked examples. This supports students in achieving the required standard by demonstrating the steps needed to reach it.',
          },
          {
            title: 'Formative assessment',
            text: 'Formative assessment informs the teaching and learning process for your students and teachers (Wiliam, 2010). Your teachers have access to live analytics that lets them formatively assess work, respond to students’ needs on the spot and determine their next teaching steps. Specific questions within each lesson match the success criteria of the lesson and allow teachers to formatively assess student learning.',
          },
          {
            title: 'Spaced repetition',
            text: 'Students need to learn the language of science before they can speak it (Cepeda, et al., 2008; Dunlosky, et al., 2013; Murre & Dros, 2015; Rohrer, 2012). Sometimes the best way to learn the multitude of terms and their definitions is with spaced repetition - Stile gives your students multiple, varying explosures to the same content.',
          },
          {
            title: 'Retrieval practice',
            text: 'Retrieval practice asks students to actively recall information in different forms to boost long-term retention and transfer knowledge to new contexts (Roediger & Karpicke, 2006). All of Stile’s resources interweave seamlessly to support students in mastering concepts through low-stake quizzes, writing topic summaries and using flashcards.',
          },
          {
            title: 'Zone of Proximal Development',
            text: 'Learning happens on the cusp of what you know and what you don’t (Vygotsky, 1978). Stile is designed to help your students find that Zone of Proximal Development and provide your teachers with the tools to prompt their students through their productive struggle.',
          },
          {
            title: 'Feedback, reflection and differentiation',
            text: 'Students receive feedback in many forms in Stile that they can use to reflect on and determine their next steps. As they work, teachers can provide verbal feedback to guide students’ progress. Upon submission, Stile marks student work and gives automated feedback. Teachers can also leave additional feedback to differentiate the learning material and support students in achieving learning goals.',
          },
          {
            title: 'Activating prior knowledge',
            text: 'Every student contributes a unique history to the classroom environment. To effectively build on their current understanding, learners must forge connections between their past experiences and the new information they encounter (Neuuman, et al., 2014). Stile units include opportunities for your students to reflect on their current knowledge and experiences, providing a sound foundation from which they can build new learning.',
          },
        ]}
      />

      <TextBlock variant="dark" title="References" space={theme.space.m}>
        <P>
          Bybee, R. W., Taylor, J. A., Gardner, A., Van Scotter, P., Powell, J. C., Westbrook, A., &
          Landes, N. (2006). The BSCS 5E instructional model: Origins and effectiveness. Colorado
          Springs, Co: BSCS, 5, 88–98.
        </P>
        <P>
          Cepeda, N. J., Vul, E., Rohrer, D., Wixted, J. T., & Pashler, H. (2008). Spacing effects
          in learning a temporal ridgeline of optimal retention. <em>Psychological Science</em>,
          19(11), 1095–1102.
        </P>
        <P>
          Dunlosky, J., Rawson, K. A., Marsh, E. J., Nathan, M. J., & Willingham, D. T. (2013).
          Improving students’ learning with effective learning techniques: Promising directions from
          cognitive and educational psychology.{' '}
          <em>Psychological Science in the Public Interest</em>, 14(1), 4–58.
        </P>
        <P>
          Hattie, J. (2012). <em>Visible learning for teachers: Maximizing impact on learning</em>.
          Routledge.
        </P>
        <P>
          Khattab, N. (2015). Students’ aspirations, expectations and school achievement: what
          really matters? <em>British Research Education Journal</em>, 41(5), 731–748.
        </P>
        <P>
          Mueller, P. A., & Oppenheimer, D. M. (2014). The pen is mightier than the keyboard:
          Advantages of longhand over laptop note taking. <em>Psychological Science</em>, 25(6),
          1159–1168. https://doi.org/10.1177/0956797614524581.
        </P>
        <P>
          Murre, J. M., & Dros, J. (2015). Replication and analysis of Ebbinghaus’ forgetting curve.{' '}
          <em>PLoS One</em>, 10(7), e0120644.
        </P>
        <P>
          National Research Council. (2012).{' '}
          <em>
            A framework for K-12 science education: Practices, crosscutting concepts, and core ideas
          </em>
          . National Academies Press.
        </P>
        <P>
          Neuman, S. B., Kaefer, T., & Pinkham, A. (2014). Building background knowledge.{' '}
          <em>The Reading Teacher</em>, 68(2), 145–148.
        </P>
        <P>
          Perry, J., Lundie, D., & Golder, G. (2019). Metacognition in schools: What does the
          literature suggest about the effectiveness of teaching metacognition in schools?{' '}
          <em>Educational Review</em>, 71(4), 483–500.
        </P>
        <P>
          Ritchhart, R., Church, M., & Morrison, K. (2011).{' '}
          <em>
            Making thinking visible: How to promote engagement, understanding, and independence for
            all learners
          </em>
          . John Wiley & Sons.
        </P>
        <P>
          Roediger, H. L. & Karpicke, J. D. (2006). Test-enhanced learning: taking memory tests
          improves long-term retention. <em>Psychological Science</em>. 17(3), 249–255. https://
          doi.org/10.1111/j.1467-9280.2006.01693.x.
        </P>
        <P>
          Rohrer, D. (2012). Interleaving helps students distinguish among similar concepts.{' '}
          <em>Educational Psychology Review</em>, 24(3), 355–367.
        </P>
        <P>
          Skipper, Y., & Douglas, K. (2015).{' '}
          <em>
            The influence of teacher feedback on children’s perceptions of student–teacher
            relationships
          </em>
          . British Journal of Educational Psychology, 85(3), 276–288.
        </P>
        <P>
          Vygotsky, L.S. (1978).{' '}
          <em>Mind in society: The development of higher psychological processes</em>. Harvard
          University Press.
        </P>
        <P>
          Wiliam, D. (2010). The role of formative assessment in effective learning environments.{' '}
          <em>The Nature of Learning: Using research to inspire practice</em>, 135–155.
        </P>
      </TextBlock>

      <NextPageBlock path="/why-choose-stile/high-impact-teaching-strategies/" />

      <CtaBlock />
    </PageLayout>
  );
}

export default EvidenceBasedPedagogyPage;
